import type { CityConfig } from "../city-template/types";

export const hendersonConfig: CityConfig = {
  city: "Henderson",
  state: "Kentucky",
  stateAbbr: "KY",
  slug: "henderson",
  route: "/cities/henderson",
  title: "Emergency Passport Henderson, KY | Fast Passport Renewal Services",
  metaDescription: "Emergency passport and expedited passport services in Henderson, KY — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Henderson County and Western Kentucky travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Henderson, KY",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Henderson and Western Kentucky travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Henderson%20Kentucky%20riverfront%20downtown%20at%20golden%20hour%20with%20Audubon%20State%20Park%2C%20Ohio%20River%20waterfront%2C%20historic%20river%20city%20district%2C%20warm%20western%20Kentucky%20sunset%20light%2C%20charming%20small%20town%20atmosphere%20with%20tree-lined%20riverfront%20streets%2C%20clean%20modern%20cityscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20vibrant%20Henderson%20County%20scene%20with%20scenic%20riverfront%20parks%20and%20historic%20architecture&width=1800&height=700&seq=henderson-hero-001&orientation=landscape",
  heroImageAlt: "Henderson Kentucky riverfront downtown at golden hour with Audubon State Park and Ohio River waterfront",
  geoPosition: "37.8362;-87.5900",
  geoPlacename: "Henderson, Kentucky",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "230 S Dearborn St, Suite 3800, Chicago, IL 60604",
    distance: "Located in downtown Chicago — approximately 270 miles from Henderson via I-69",
  },
  nearbyCitiesZips: [
    { name: "Henderson", zips: "42420, 42419" },
    { name: "Owensboro", zips: "42301, 42302, 42303" },
    { name: "Evansville (IN)", zips: "47701–47750" },
    { name: "Newburgh (IN)", zips: "47629, 47630" },
    { name: "Princeton", zips: "42445" },
    { name: "Madisonville", zips: "42431" },
    { name: "Morganfield", zips: "42437" },
    { name: "Mount Vernon (IN)", zips: "47620" },
    { name: "Mount Carmel (IL)", zips: "62863" },
    { name: "Sebree", zips: "42455" },
    { name: "Providence", zips: "42450" },
    { name: "Dixon", zips: "42409" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Henderson County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Louisville", route: "/cities/louisville" },
    { name: "Fast passport renewal in Owensboro", route: "/cities/owensboro" },
    { name: "Emergency passport help in Evansville", route: "/evansville" },
    { name: "Urgent passport services in Madisonville", route: "/madisonville" },
    { name: "Passport courier services near Princeton", route: "/princeton" },
    { name: "Same-day passport help in Paducah", route: "/cities/paducah" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Henderson remotely via secure courier. We do not have a walk-in office in Henderson — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Henderson County travelers.",
  fpcOfficeDistance: "Serving all of Henderson County and Western Kentucky",
  serviceAreaIntro: "From downtown Henderson's historic riverfront to the Audubon State Park trails, from the Evansville metro area to the surrounding Kentucky-Indiana border region — Fast Passport Center serves the entire Henderson community. We regularly help travelers from Owensboro, Evansville, and Newburgh get their passports fast.",
  nearbyCitiesIntro: "We serve all Henderson neighborhoods and nearby Western Kentucky and Indiana cities including Owensboro, Evansville (IN), Newburgh (IN), Princeton, Madisonville, Morganfield, Mount Vernon (IN), Mount Carmel (IL), Sebree, Providence, and Dixon. If you are in Henderson County, western Kentucky, or the tri-state area, we have you covered.",
  howItWorksIntro: "Most Henderson travelers complete everything online and ship documents overnight. No need to drive to Chicago or wait in line at the regional agency.",
  mistakesIntro: "Henderson travelers often face long drives to the nearest regional passport agency. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Henderson and Henderson County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Henderson? Here are the most common ones we hear from Henderson County travelers.",
  statePageRoute: "/states/kentucky",
  statePageName: "Kentucky",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Henderson County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Carol A.",
      location: "Henderson, KY",
      rating: 5,
      text: "Emergency trip to London for my daughter's wedding. They got me a passport in 36 hours. I walked her down the aisle and didn't miss a moment.",
      service: "Emergency Passport",
    },
    {
      name: "John P.",
      location: "Evansville, IN",
      rating: 5,
      text: "Business trip to Frankfurt for a manufacturing deal. My passport was expired by 3 months. They got me a renewal in 4 days and I closed the deal on time.",
      service: "Passport Renewal",
    },
    {
      name: "Maria G.",
      location: "Henderson, KY",
      rating: 5,
      text: "First-time passport for our Mediterranean cruise. They helped me find the nearest acceptance facility and I had my passport in 5 days. The process was so smooth.",
      service: "New Passport",
    },
    {
      name: "Thomas H.",
      location: "Newburgh, IN",
      rating: 5,
      text: "Lost my passport before my family trip to Cancun. They got me a replacement in 24 hours. My kids got their beach vacation and I couldn't be more grateful.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "henderson-1",
      name: "Henderson Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "100 N Main St",
      city: "Henderson",
      state: "KY",
      zip: "42420",
      phone: "(270) 826-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from downtown Henderson",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for Henderson County. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "henderson-2",
      name: "Owensboro Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "51 E 4th St",
      city: "Owensboro",
      state: "KY",
      zip: "42303",
      phone: "(270) 684-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Owensboro",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Daviess County and eastern Henderson County. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "henderson-3",
      name: "Evansville Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "800 Sycamore St",
      city: "Evansville",
      state: "IN",
      zip: "47708",
      phone: "(812) 423-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Evansville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for the Evansville metro and Indiana side of the river. Full passport services with appointment.",
      icon: "ri-mail-line",
    },
    {
      id: "henderson-4",
      name: "Newburgh Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "5410 State Route 261",
      city: "Newburgh",
      state: "IN",
      zip: "47630",
      phone: "(812) 858-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Newburgh",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Warrick County and Indiana travelers. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "henderson-5",
      name: "Princeton Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "100 E Main St",
      city: "Princeton",
      state: "KY",
      zip: "42445",
      phone: "(270) 365-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from downtown Princeton",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Caldwell County and southern Henderson County. Call for appointment availability.",
      icon: "ri-mail-line",
    },
    {
      id: "henderson-6",
      name: "Henderson County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "20 N Main St",
      city: "Henderson",
      state: "KY",
      zip: "42420",
      phone: "(270) 831-1201",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.1 miles from Henderson County Courthouse",
      website: "https://www.hendersonky.us/",
      websiteLabel: "Henderson County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 4.5 hour drive to Chicago", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Henderson County photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Henderson County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Henderson applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Chicago regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Henderson County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Henderson County travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Henderson." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Henderson address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Henderson before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Henderson for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the 4.5 hour drive to Chicago." },
    { q: "How fast can I renew my passport in Henderson?", a: "Fast Passport Center offers fast passport renewal in Henderson in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. Many cruise lines require your passport to be valid for at least 6 months beyond your travel date. We can expedite your passport renewal in Henderson in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Henderson?", a: "Same-day passport appointments are only available at the Chicago Regional Passport Agency — a 4.5 hour drive from Henderson — for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Henderson County residents." },
    { q: "Can I expedite a child passport in Henderson?", a: "Yes. Fast Passport Center expedites child passports in Henderson for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk Henderson County families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Henderson with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "How fast can I get a passport in Henderson?", a: "With Fast Passport Center's emergency courier service, Henderson travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays." },
    { q: "Do I need to drive to the Chicago Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "Where are the closest passport acceptance facilities in Henderson?", a: "Popular options include the Henderson Post Office (42420), Owensboro Post Office (42303), Evansville Main Post Office (IN), Newburgh Post Office (IN), Princeton Post Office (42445), and Henderson County Clerk (42420). All require appointments. We help you find the most convenient location and schedule your visit." },
    { q: "Can I get a same-day passport in Henderson?", a: "Same-day passports are only available at the Chicago Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option — without the 4.5 hour drive to Chicago." },
    { q: "What if I live in a smaller Henderson County town?", a: "We serve the entire Henderson County and surrounding area including Owensboro, Evansville (IN), Newburgh (IN), Princeton, Madisonville, Morganfield, Mount Vernon (IN), Mount Carmel (IL), Sebree, Providence, and Dixon. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed." },
    { q: "How much does expedited passport service cost in Henderson?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline." },
    { q: "Do you help with passport photos in Henderson?", a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and Walmart locations in Henderson, Owensboro, and Evansville offer passport photo services. We review your photos before submission to ensure they meet State Department standards." },
    { q: "What documents do I need for a first-time passport in Henderson?", a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk." },
    { q: "Can you help with a child's passport in Henderson?", a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Henderson County families through the entire process and help schedule acceptance clerk appointments." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Henderson", address: "2750 US Highway 41 N, Henderson, KY 42420", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Henderson", address: "2220 US Highway 41 N, Henderson, KY 42420", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Walmart — Henderson", address: "2800 US Highway 41 N, Henderson, KY 42420", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Henderson", address: "1300 N Green St, Henderson, KY 42420", hours: "Mon–Sun: 8AM–10PM" },
    { name: "CVS Pharmacy — Evansville", address: "4500 N First Ave, Evansville, IN 47710", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walmart — Evansville", address: "401 N Burkhardt Rd, Evansville, IN 47715", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Walgreens — Owensboro", address: "3332 Frederica St, Owensboro, KY 42301", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Evansville", address: "600 N Green River Rd, Evansville, IN 47715", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "Circuit Court Clerk — Henderson County",
      address: "20 N Main St, Henderson, KY 42420",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Driver's license and REAL ID. Appointment recommended.",
      featured: true,
    },
    {
      name: "Circuit Court Clerk — Daviess County",
      address: "212 Saint Ann St, Owensboro, KY 42303",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "REAL ID and driver's license renewals. Online booking available.",
      featured: false,
    },
    {
      name: "Circuit Court Clerk — Warrick County (IN)",
      address: "1 County Square, Boonville, IN 47601",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Full-service center for Indiana residents in the tri-state area.",
      featured: true,
    },
    {
      name: "Circuit Court Clerk — Caldwell County",
      address: "100 E Market St, Princeton, KY 42445",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Alternative for Caldwell County residents. Walk-ins limited.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "EVV",
      name: "Evansville Regional Airport",
      distance: "12 miles from downtown Henderson",
      address: "7801 Bussing Rd, Evansville, IN 47725",
      note: "The primary airport for Henderson County. Regional flights with connections to Chicago, Dallas, and Orlando. Accessible via US-41 bridge.",
      flightUrl: "https://www.flyevv.com/",
      flightLabel: "EVV Airport",
      featured: true,
    },
    {
      code: "OWB",
      name: "Owensboro-Daviess County Regional",
      distance: "30 miles from Henderson",
      address: "2200 Airport Rd, Owensboro, KY 42301",
      note: "Alternative regional airport for Daviess County and western Henderson. Regional flights available.",
      flightUrl: "https://www.owensboroairport.com/",
      flightLabel: "OWB Airport",
      featured: false,
    },
    {
      code: "SDF",
      name: "Louisville Muhammad Ali International",
      distance: "125 miles from Henderson",
      address: "600 Terminal Dr, Louisville, KY 40209",
      note: "Alternative regional airport for international flights. Accessible via I-69.",
      flightUrl: "https://www.flylouisville.com/",
      flightLabel: "Louisville Airport",
      featured: false,
    },
  ],
};